import * as yup from 'yup';
import type { DrinkingPreference, InterestPreference, MeasurementSystem, ClassPreference, PillowPreference, RoomPreference, SpaPreference, SeatPreference, RelationshipType } from '@/types/clientsApiTypes';
import { DRINKING_PREFERENCE_OPTIONS, INTEREST_PREFERENCE_OPTIONS, PILLOW_PREFERENCE_OPTIONS, ROOM_PREFERENCE_OPTIONS, SEAT_PREFERENCES, SPA_PREFERENCE_OPTIONS, CLASS_PREFERENCE_OPTIONS, RELATIONSHIP_TYPES } from '@/types/clientsApiTypes';
import { ACCOMMODATION_LEGACY_ENTRY_MAX_LENGTH, isKnownAccommodationPreferenceKey } from '@/utils/preferences/partitionAccommodationTypes';
import { emailValidation, nullableString, nullableNumber, validatePhoneNumber } from '@/utils/validations';
import { FIRST_NAME_MAX_LENGTH, firstNameMaxMessage, LAST_NAME_MAX_LENGTH, lastNameMaxMessage, MIDDLE_NAME_MAX_LENGTH, middleNameMaxMessage } from '@/utils/validations/client-validations';

export const relatedPassengerFirstNameField = () =>
  yup.string().optional().defined().max(FIRST_NAME_MAX_LENGTH, firstNameMaxMessage);
export const relatedPassengerMiddleNameField = () =>
  nullableString().max(MIDDLE_NAME_MAX_LENGTH, middleNameMaxMessage).optional().defined();
export const relatedPassengerLastNameField = () =>
  yup.string().optional().defined().max(LAST_NAME_MAX_LENGTH, lastNameMaxMessage);

export const primaryPassengerFirstNameField = () =>
  yup.string().max(FIRST_NAME_MAX_LENGTH, firstNameMaxMessage).required('First name is required');
export const primaryPassengerMiddleNameField = () =>
  nullableString().max(MIDDLE_NAME_MAX_LENGTH, middleNameMaxMessage).defined();
export const primaryPassengerLastNameField = () =>
  yup.string().max(LAST_NAME_MAX_LENGTH, lastNameMaxMessage).required('Last name is required');

const documentSchema = yup.object({
  id: yup.number().required(),
  client_id: nullableNumber().defined(),
  document_name: yup.string().required(),
  document_type: yup.mixed<'passport' | 'id'>().oneOf(['passport', 'id']).required('Document type is required'),
  document_url: nullableString().defined(),
  passport_number: nullableString().defined(),
  expiry_date: nullableString().defined(),
  issuance_country: nullableString().defined(),
  issue_date: nullableString().defined(),
  created_at: nullableString().defined(),
  updated_at: nullableString().defined(),
});

const loyaltyNumberSchema = yup.object({
  lp_id: nullableNumber().defined(),
  category_id: nullableNumber().defined(),
  number: nullableString().defined(),
  is_legacy: yup.boolean().defined(),
  loyalty_program: nullableString().defined(),
  frequent_flyer_number: nullableString().defined(),
  note: nullableString().defined(),
});

const relatedPassengerSchema = yup.object({
  client: yup.object({
    client_id: yup.number().optional().defined(),
    userHasCertifiedTheInformation: yup
      .boolean()
      .oneOf([true, false], 'Please certify the information to proceed')
      .optional()
      .defined()
      .default(false),
    preferred_name: nullableString().optional().defined(),
    first_name: relatedPassengerFirstNameField(),
    middle_name: relatedPassengerMiddleNameField(),
    last_name: relatedPassengerLastNameField(),
    gender: nullableString().optional(),
    relationship: yup
      .string()
      .optional()
      .defined()
      .nullable()
      .default(null)
      .test('is-valid-relationship', 'Unsupported relationship. Please select a new one.', (value) => {
        if (!value) return true;
        return Object.values(RELATIONSHIP_TYPES).includes(value as RelationshipType);
      }),
    date_of_birth: yup.string().optional().defined().nullable(),
    contact_primary_client_id: yup.number().optional().defined().nullable().default(null),
    email: yup
      .string()
      .email('Invalid email')
      .test('is-valid-email', 'Invalid email', (value) => {
        return emailValidation(value ?? '');
      })
      .optional()
      .defined(),
    additional_emails: yup
      .array()
      .of(
        yup.object({
          email: yup
            .string()
            .email('Invalid email')
            .test('is-valid-email', 'Invalid email', (value) => {
              return emailValidation(value ?? '');
            })
            .required('Email is required'),
        })
      )
      .required()
      .default([]),
    phone: nullableString()
      .optional()
      .test('valid-phone-format', 'Invalid phone number', (value) => validatePhoneNumber(value ?? null)),
    phones: yup
      .array()
      .of(
        yup.object({
          phone: yup
            .string()
            .optional()
            .defined()
            .test(
              'valid-phone-format',
              'Invalid phone number',
              (value) => !value || value.trim() === '' || validatePhoneNumber(value)
            ),
          title: yup.string().nullable().defined(),
        })
      )
      .required()
      .default([]),
    client_mailing_address: nullableString().optional().defined(),
    client_mailing_address_line_2: nullableString().optional(),
    client_mailing_city: nullableString().optional().defined(),
    client_mailing_state: nullableString().optional().defined(),
    client_mailing_country: nullableString().optional().defined(),
    zip_code: nullableString()
      .optional()
      .defined()
      .max(20, 'The client zip code field must not be greater than 20 characters.'),
    same_as_primary: yup.boolean().optional().defined().default(false),
  }),
  tsa: yup.object({
    global_entry: nullableString().defined(),
    global_expiry_date: nullableString().defined(),
    known_traveler_number: nullableString().defined(),
    known_traveler_number_expire_date: nullableString().defined(),
  }),
  passport: yup.object({
    passport_number: nullableString().defined(),
    expiry_date: nullableString().defined(),
    passport_id: nullableNumber().defined(),
  }),
  preference_data: yup.object({
    allergies: nullableString().defined(),
    fly: yup
      .object({
        seat: yup.mixed<SeatPreference>().oneOf(Object.values(SEAT_PREFERENCES)).nullable().default(null),
        allergies: nullableString().defined(),
        preferred_private_aviation_company: nullableString().defined(),
        fly_private_aviation: yup.mixed<'yes' | 'no'>().oneOf(['yes', 'no']).required().nullable().default(null),
        class_preference: yup
          .array()
          .of(
            yup
              .mixed<ClassPreference>()
              .oneOf(CLASS_PREFERENCE_OPTIONS.map((o) => o.key))
              .required()
          )
          .nullable()
          .default(null),
      })
      .nullable()
      .default(null)
      .defined(),
    personal: yup.object({
      personal_metric_system: yup.mixed<MeasurementSystem>().oneOf(['metric', 'us']).required().default('metric'),
      comfort_related: yup.object({
        us: yup.object({
          height: nullableString().defined(),
          weight: nullableString().defined(),
        }),
        metric: yup.object({
          height: nullableString().defined(),
          weight: nullableString().defined(),
        }),
      }),
      information: nullableString().defined(),
    }),
  }),
  documents: yup.array().of(documentSchema).optional().default([]),
  loyalty_numbers: yup.array().of(loyaltyNumberSchema).required().default([]),
});

export const createClientSchema = yup.object().shape({
  primary_passenger: yup.object().shape({
    client: yup.object().shape({
      client_id: yup.number().required().default(0),
      userHasCertifiedTheInformation: yup
        .boolean()
        .oneOf([true, false], 'Please certify the information to proceed')
        .optional()
        .default(false),
      preferred_name: nullableString().defined(),
      profile_picture_url: nullableString().optional(),
      first_name: primaryPassengerFirstNameField(),
      middle_name: primaryPassengerMiddleNameField(),
      last_name: primaryPassengerLastNameField(),
      is_vip: yup.boolean().required().default(false),
      gender: nullableString().defined().optional(),
      source_id: yup.number().required().default(0),
      email: yup
        .string()
        .email('Invalid email')
        .test('is-valid-email', 'Invalid email', (value) => {
          return emailValidation(value ?? '');
        })
        .required('Email is required'),
      additional_emails: yup
        .array()
        .of(
          yup.object({
            email: yup
              .string()
              .email('Invalid email')
              .test('is-valid-email', 'Invalid email', (value) => {
                return emailValidation(value ?? '');
              })
              .required('Email is required'),
          })
        )
        .required()
        .default([]),
      date_of_birth: nullableString().optional(),
      phone: nullableString()
        .optional()
        .test('valid-phone-format', 'Invalid phone number', (value) => validatePhoneNumber(value)),
      client_mailing_address: nullableString().optional(),
      client_mailing_address_line_2: nullableString().optional(),
      client_mailing_city: nullableString().optional(),
      client_mailing_state: nullableString().optional(),
      client_mailing_country: nullableString().optional(),
      zip_code: nullableString()
        .optional()
        .max(20, 'The client zip code field must not be greater than 20 characters.'),
      is_billing_same_as_mailing: yup.boolean().optional(),
      client_billing_address: nullableString().optional(),
      client_billing_address_line_2: nullableString().optional(),
      client_billing_city: nullableString().optional(),
      client_billing_state: nullableString().optional(),
      client_billing_country: nullableString().optional(),
      billing_zip_code: nullableString().optional(),
      social_facebook_url: nullableString().url('Invalid URL').defined(),
      social_instagram_url: nullableString().url('Invalid URL').defined(),
      social_linkedin_url: nullableString().url('Invalid URL').defined(),
      social_twitter_url: nullableString().url('Invalid URL').defined(),
      social_custom_url: nullableString().url('Invalid URL').defined(),
      phones: yup
        .array()
        .of(
          yup.object({
            phone: yup
              .string()
              .optional()
              .defined()
              .test(
                'valid-phone-format',
                'Invalid phone number',
                (value) => !value || value.trim() === '' || validatePhoneNumber(value)
              ),
            title: yup.string().nullable().defined(),
          })
        )
        .required()
        .default([]),
      preferred_airport_ids: yup.array().of(yup.number().required()).required().default([]),
      profile_picture_storage_id: nullableString().defined(),
      emergency_contact_name: nullableString().optional(),
      emergency_relationship_type_id: nullableNumber().optional(),
      emergency_relationship: yup
        .object({
          label: yup.string().optional().defined(),
          value: yup.number().nullable().optional().defined(),
        })
        .nullable()
        .defined(),
      emergency_contact_phone: nullableString().optional(),
      emergency_contact_email: nullableString()
        .optional()
        .test('is-valid-email', 'Invalid email', (value) => !value || value.trim() === '' || emailValidation(value)),
    }),
    documents: yup.array().of(documentSchema).required().default([]),
    preference_data: yup.object().shape({
      allergies: nullableString().defined(),
      fly: yup
        .object()
        .shape({
          preferred_private_aviation_company: nullableString().defined(),
          fly_private_aviation: yup.mixed<'yes' | 'no'>().oneOf(['yes', 'no']).nullable().default(null),
          seat: yup.mixed<'Window' | 'Aisle'>().oneOf(['Window', 'Aisle']).required().default('Window'),
          class_preference: yup
            .array()
            .of(
              yup
                .mixed<ClassPreference>()
                .oneOf(CLASS_PREFERENCE_OPTIONS.map((o) => o.key))
                .required()
            )
            .nullable()
            .default(null),
        })
        .nullable()
        .default(null)
        .defined(),
      travel: nullableString().defined(),
      stay: yup
        .object()
        .shape({
          accommodation_types: yup
            .array()
            .of(
              yup
                .string()
                .required()
                .test(
                  'accommodation-standard-or-legacy',
                  `Each entry must be a standard accommodation option or legacy text (max ${ACCOMMODATION_LEGACY_ENTRY_MAX_LENGTH} characters)`,
                  (value) => {
                    if (value == null || value === '') return false;
                    if (isKnownAccommodationPreferenceKey(value)) return true;
                    return value.length <= ACCOMMODATION_LEGACY_ENTRY_MAX_LENGTH;
                  }
                )
            )
            .nullable()
            .default(null),
          preferences_note: nullableString().defined(),
        })
        .required(),
      general_interests: yup
        .array()
        .of(
          yup
            .mixed<InterestPreference>()
            .oneOf(INTEREST_PREFERENCE_OPTIONS.map((o) => o.key))
            .required()
        )
        .nullable()
        .default(null),
      beverage_preferences: yup
        .array()
        .of(
          yup
            .mixed<DrinkingPreference>()
            .oneOf(DRINKING_PREFERENCE_OPTIONS.map((o) => o.key))
            .required()
        )
        .nullable()
        .default(null),
      pillow_preferences: yup
        .array()
        .of(
          yup
            .mixed<PillowPreference>()
            .oneOf(PILLOW_PREFERENCE_OPTIONS.map((o) => o.key))
            .required()
        )
        .nullable()
        .default(null),
      room_configuration_preferences: yup
        .array()
        .of(
          yup
            .mixed<RoomPreference>()
            .oneOf(ROOM_PREFERENCE_OPTIONS.map((o) => o.key))
            .required()
        )
        .nullable()
        .default(null),
      spa_preferences: yup
        .array()
        .of(
          yup
            .mixed<SpaPreference>()
            .oneOf(SPA_PREFERENCE_OPTIONS.map((o) => o.key))
            .required()
        )
        .nullable()
        .default(null),
      spa_preferences_special_requests: nullableString().defined(),
      likes: nullableString().defined(),
      dislikes: nullableString().defined(),
      personal: yup.object().shape({
        personal_metric_system: yup.mixed<MeasurementSystem>().oneOf(['metric', 'us']).required().default('metric'),
        information: nullableString().defined(),
        comfort_related: yup
          .object()
          .shape({
            us: yup
              .object()
              .shape({
                height: nullableString().defined(),
                weight: nullableString().defined(),
              })
              .required()
              .default({ height: null, weight: null }),
            metric: yup
              .object()
              .shape({
                height: nullableString().defined(),
                weight: nullableString().defined(),
              })
              .required()
              .default({ height: null, weight: null }),
          })
          .required(),
      }),
    }),
    tsa: yup.object().shape({
      known_traveler_number: nullableString().defined(),
      known_traveler_number_expire_date: nullableString().defined(),
      global_entry: nullableString().defined(),
      global_expiry_date: nullableString().defined(),
    }),
    passport: yup.object().shape({
      passport_number: nullableString().defined(),
      expiry_date: nullableString().defined(),
      passport_id: nullableNumber().defined(),
    }),
    selected_airports: yup
      .array()
      .of(
        yup.object({
          label: yup.string().required('Airport is required'),
          value: yup
            .number()
            .nullable()
            .defined()
            .test('required', 'Airport is required', (val) => val != null),
        })
      )
      .required()
      .default([]),
    loyalty_numbers: yup.array().of(loyaltyNumberSchema).required().default([]),
  }),

  related_passengers: yup.array().of(relatedPassengerSchema).default([]),
});

export const REQUIRED_FORM_KEYS = [
  'primary_passenger.client.first_name',
  'primary_passenger.client.last_name',
  'primary_passenger.client.email',
] as const;

export const REQUIRED_RELATED_PASSENGER_KEYS = [] as const; // no required fields for related passengers

// Name fields carry max-length rules but are not all in the required lists above.
// These are validated for submit gating so an over-limit name blocks submission.
export const PRIMARY_PASSENGER_NAME_KEYS = [
  'primary_passenger.client.first_name',
  'primary_passenger.client.middle_name',
  'primary_passenger.client.last_name',
] as const;

export const RELATED_PASSENGER_NAME_KEYS = ['client.first_name', 'client.middle_name', 'client.last_name'] as const;

export const SKIP_KEYS_FOR_CONFIRMATION_MODAL = [
  'primary_passenger.client.profile_picture_storage_id',
  'primary_passenger.client.profile_picture_url',
  'primary_passenger.client.social_custom_url',
  'primary_passenger.client.social_facebook_url',
  'primary_passenger.client.social_instagram_url',
  'primary_passenger.client.social_linkedin_url',
  'primary_passenger.client.social_twitter_url',
  'primary_passenger.preference_data.dislikes',
  'primary_passenger.client.phones',
  'primary_passenger.preference_data.fly',
] as const;

export const CHECK_ONLY_LENGTH_FIELDS = ['primary_passenger.loyalty_numbers'] as const;

export const CONDITIONAL_FIELDS = {
  'primary_passenger.preference_data.fly.fly_private_aviation': {
    yes: ['primary_passenger.preference_data.fly.preferred_private_aviation_company'],
    no: ['primary_passenger.preference_data.fly.class_preference', 'primary_passenger.preference_data.fly.seat'],
  },
};